import { Plus } from "lucide-react";
import { cn } from "../../lib/cn";
import { FOCUS_RING } from "../../lib/focusRing";
import { Sparkle } from "../decor";
import { Scene } from "./stage/Scene";
import { SplitChars } from "./stage/Split";
import * as fx from "./stage/fx";
import type { Timeline } from "./stage/choreography";

const FAQS = [
  {
    q: "Does the caregiver need an account?",
    a: "No. They open the link you send and see a clean, read-only care sheet — no app, no login, no sign-up.",
  },
  {
    q: "Can I make a list for each of my kids?",
    a: "Yes. Every child gets their own profile, and you choose which child each link shares.",
  },
  {
    q: "Can I take a link back?",
    a: "Anytime. Revoke a link or let it expire, and it stops working immediately for everyone who has it.",
  },
  {
    q: "What can a caregiver actually see?",
    a: "Only the sections you chose when you created the link. Everything else is never included in the page they receive.",
  },
  {
    q: "Is my family's information secure?",
    a: "Yes. Links use long random tokens stored only as hashes, traffic is encrypted, and we never store visitors' IP or device details in readable form.",
  },
  {
    q: "What does it cost?",
    a: "Creating an account and your first profile is free — set it up in a few minutes.",
  },
];

/*
 * Signature: paper unfold. The headline waves in letter by letter, then the
 * questions open like a folded paper list: each strip swings down on the
 * crease it shares with the strip above, one after another.
 */
const TIMELINE: Timeline = [
  fx.fade("[data-fx=eyebrow]", { duration: 0.35 }),
  fx.wave("[data-fx=title] [data-char]", { stagger: 0.022, at: "-0.2" }),
  fx.pop("[data-fx=twinkle]", { from: 0, stagger: 0.12, at: "-0.35" }),
  fx.fade("[data-fx=sub]", { at: "-0.4" }),
  fx.unfold("[data-fx=row]", { duration: 0.55, stagger: 0.17, at: "-0.25" }),
];

export function FAQ() {
  const last = FAQS.length - 1;

  return (
    <Scene id="faq" label="FAQ" timeline={TIMELINE} className="bg-gradient-to-b from-white to-sky-50/60">
      {/* Backdrop: soft colour fields and a whisper of grain. */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute -right-40 -top-32 h-[28rem] w-[28rem] rounded-full bg-sky-200/40 blur-3xl" />
        <div className="absolute -left-32 bottom-0 h-96 w-96 rounded-full bg-indigo-100/60 blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 h-64 w-64 rounded-full bg-amber-100/40 blur-3xl" />
        <div className="bg-grain absolute inset-0 opacity-[0.05] mix-blend-multiply" />
      </div>

      <div className="mx-auto grid w-full max-w-6xl flex-1 content-center items-center gap-6 px-6 pb-10 pt-20 sm:gap-10 lg:grid-cols-[0.8fr_1.2fr] lg:gap-16">
        {/* Copy */}
        <div>
          <p data-fx="eyebrow" className="text-sm font-semibold uppercase tracking-wide text-sky-700">
            FAQ
          </p>
          <h2
            data-fx="title"
            className="mt-2 font-display text-3xl font-bold tracking-tight text-neutral-900 sm:text-4xl lg:text-5xl"
          >
            <SplitChars text="Questions," />{" "}
            {/* The last word carries the twinkles, so they stay at its end wherever the line wraps. */}
            <span className="relative inline-block">
              <SplitChars text="answered." />
              <span aria-hidden="true">
                <span
                  data-fx="twinkle"
                  className="absolute -right-7 top-0 h-5 w-5 text-amber-300 sm:-right-10 sm:h-7 sm:w-7"
                >
                  <Sparkle className="h-full w-full animate-pulse" />
                </span>
                <span
                  data-fx="twinkle"
                  className="absolute -right-11 -top-2 h-3 w-3 text-sky-400 sm:-right-16 sm:-top-3 sm:h-4 sm:w-4"
                >
                  <Sparkle className="h-full w-full animate-pulse" style={{ animationDelay: "1.2s" }} />
                </span>
              </span>
            </span>
          </h2>
          <p data-fx="sub" className="mt-3 max-w-md text-[15px] text-neutral-600 sm:mt-4 sm:text-lg">
            Everything families ask before they start. Still curious?{" "}
            <a
              href="#feedback"
              className={cn(
                "rounded font-medium text-sky-700 underline decoration-sky-300 decoration-2 underline-offset-4 transition-colors hover:text-sky-800 hover:decoration-sky-600",
                FOCUS_RING,
              )}
            >
              Send us a note
            </a>{" "}
            below.
          </p>
        </div>

        {/* One paper sheet, folded into strips. A drop-shadow (not a box
            shadow) follows the strips as they unfold, so no empty card
            outline shows before they arrive. */}
        <div className="drop-shadow-[0_18px_24px_rgba(2,132,199,0.12)]">
          {FAQS.map((f, i) => (
            <div
              key={f.q}
              data-fx="row"
              className={cn(
                // A faint fall-off toward each crease is what makes the strips read as folded paper.
                "overflow-hidden border-x border-b border-sky-100 bg-gradient-to-b from-white via-white to-sky-50/50",
                i === 0 && "rounded-t-3xl border-t",
                i === last && "rounded-b-3xl",
              )}
            >
              {/* Sharing a `name` makes the list an exclusive accordion
                  (opening one closes the other), so it always fits the screen. */}
              <details name="faq" className="group transition-colors open:bg-sky-50/70">
                <summary className="flex min-h-[52px] cursor-pointer list-none items-center justify-between gap-4 px-4 py-2.5 text-[15px] font-medium text-neutral-900 outline-none transition-colors hover:text-sky-700 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-sky-500 sm:min-h-[56px] sm:px-6 sm:text-base [&::-webkit-details-marker]:hidden">
                  {f.q}
                  <span
                    aria-hidden="true"
                    className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-sky-50 text-sky-600 transition duration-200 group-open:rotate-45 group-open:bg-sky-500 group-open:text-white"
                  >
                    <Plus className="h-4 w-4" />
                  </span>
                </summary>
                {/* CSS grid 0fr → 1fr disclosure (native semantics kept) */}
                <div className="grid grid-rows-[0fr] transition-[grid-template-rows] duration-300 ease-out group-open:grid-rows-[1fr]">
                  <div className="overflow-hidden">
                    <p className="px-4 pb-4 text-sm text-neutral-600 sm:px-6 sm:pb-5 sm:text-[15px]">{f.a}</p>
                  </div>
                </div>
              </details>
            </div>
          ))}
        </div>
      </div>
    </Scene>
  );
}
